import { HTMLAttributes } from 'react'
import { default as NextLink } from 'next/link'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Link from '@/components/base/link'
import Motion from '@/components/base/motion'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'

import { singleParagraphRevealAnimation } from './animations'
import { cn } from '@/lib/utils'
import { WPLink } from '@/types'

export type SingleParagraphProps = HTMLAttributes<HTMLElement> &
  Partial<{
    overline: string
    align: 'left' | 'center' | 'right'
    title: string
    paragraph: string
    link: WPLink
  }>

const SingleParagraph = ({
  title,
  align,
  overline,
  paragraph,
  link,
  ...props
}: SingleParagraphProps) => {
  return (
    <Motion animation={singleParagraphRevealAnimation}>
      {(motionRef) => (
        <Section ref={motionRef} {...props}>
          <Container>
            <Grid className="gap-y-[1.6rem]">
              {overline ? (
                <div
                  className={cn({
                    'col-span-4 md:col-span-4 md:col-start-3 lg:col-span-10 lg:col-start-3':
                      align === 'left',
                    'col-span-4 text-center md:col-span-6 lg:col-span-8 lg:col-start-3':
                      align === 'center',
                    'col-span-4 md:col-span-4 md:col-start-3 lg:col-span-6 lg:col-start-7':
                      align === 'right'
                  })}
                >
                  <Typography
                    variant="label"
                    component="h2"
                    className="font-gillsans uppercase text-copper"
                    data-motion-overline
                  >
                    {overline}
                  </Typography>
                </div>
              ) : null}

              {title ? (
                <div
                  className={cn({
                    'col-span-4 md:col-span-4 md:col-start-2 lg:col-span-6 lg:col-start-2':
                      align === 'left',
                    'col-span-4 text-center md:col-span-6 lg:col-span-8 lg:col-start-3':
                      align === 'center',
                    'col-span-4 md:col-span-4 md:col-start-2 lg:col-span-6 lg:col-start-6':
                      align === 'right'
                  })}
                >
                  <Typography
                    variant="h4"
                    component="h3"
                    className={cn('font-timesnow -tracking-[0.0025em]')}
                    data-motion-title
                  >
                    {title}
                  </Typography>
                </div>
              ) : null}

              {paragraph ? (
                <div
                  className={cn({
                    'col-span-4 md:col-span-4 md:col-start-2 lg:col-span-6 lg:col-start-2':
                      align === 'left',
                    'col-span-4 text-center md:col-span-6 lg:col-span-8 lg:col-start-3':
                      align === 'center',
                    'col-span-4 md:col-span-4 md:col-start-2 lg:col-span-6 lg:col-start-6':
                      align === 'right'
                  })}
                >
                  <Typography
                    variant="body"
                    paragraph
                    className="font-gillsans font-light"
                    data-motion-paragraph
                  >
                    {paragraph}
                  </Typography>
                </div>
              ) : null}

              {link ? (
                <div
                  className={cn('mt-[1.6rem]', {
                    'col-span-4 md:col-span-4 md:col-start-2 lg:col-span-6 lg:col-start-2':
                      align === 'left',
                    'col-span-4 text-center md:col-span-6 lg:col-span-8 lg:col-start-3':
                      align === 'center',
                    'col-span-4 md:col-span-4 md:col-start-2 lg:col-span-6 lg:col-start-6':
                      align === 'right'
                  })}
                >
                  <Link asChild data-motion-link>
                    <NextLink href={link.url} target={link.target}>
                      <Typography variant="label">{link.title}</Typography>
                    </NextLink>
                  </Link>
                </div>
              ) : null}
            </Grid>
          </Container>
        </Section>
      )}
    </Motion>
  )
}

export default SingleParagraph
